'use client'

import React from 'react'

import { Box } from '@chakra-ui/react'
import { keyframes } from '@emotion/react'

import type { ColorScheme } from '../../types'
import { SPINNER_SIZES, SpinnerSize, spinnerColor } from './Spinner.styles'

export type { SpinnerSize }

// Chakra v3's own Spinner animates via the system's global keyframe layer,
// which is NOT injected on a Chakra-v1-hosted page (only the token vars are) —
// there it renders static. This emotion keyframe injects reliably wherever the
// component mounts, matching the pattern the dark Button / SegmentedControl use.
const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`

export type SpinnerProps = {
  /** Named size ('sm' | 'md' | 'lg' | 'xl') or any CSS length. Default 'md'. */
  size?: SpinnerSize | string
  /** Ring thickness. Default '2px'. */
  borderWidth?: string
  /** Rotation period. Default '0.7s'. */
  speed?: string
  colorScheme?: ColorScheme
  /** Token overriding the ring color. Defaults to the scheme's icon.secondary. */
  color?: string
  'aria-label'?: string
  'data-testid'?: string
}

/**
 * Circular loading spinner. A ring with one transparent edge, rotated by an
 * emotion keyframe. Color comes from a semantic token (via currentColor), so it
 * stays theme-aware in both color schemes.
 */
export const Spinner = ({
  size = 'md',
  borderWidth = '2px',
  speed = '0.7s',
  colorScheme = 'dark',
  color,
  'aria-label': ariaLabel = 'Loading',
  'data-testid': dataTestId,
}: SpinnerProps) => {
  const dimension = SPINNER_SIZES[size as SpinnerSize] ?? size

  return (
    <Box
      animation={`${spin} ${speed} linear infinite`}
      aria-label={ariaLabel}
      border={`${borderWidth} solid currentColor`}
      borderRadius='full'
      borderTopColor='transparent'
      color={color ?? spinnerColor(colorScheme)}
      data-testid={dataTestId}
      display='inline-block'
      flexShrink={0}
      h={dimension}
      role='status'
      w={dimension}
    />
  )
}
